import { useCallback, useEffect, useRef, useState } from "react"
import { string } from "prop-types"
import { StackView } from "@planningcenter/tapestry-react"
import { Loading } from "components/ui"
import useReservation from "./hooks/use_reservation"
import RegisterableContext from "components/admin/registerable/context"
import Errors from "components/admin/registerable/Errors"
import RegistrationContact from "components/admin/registerable/RegistrationContact"
import Selections from "./Selections"
import Attendees from "./Attendees"
import Footer from "./Footer"
import useApiClient from "hooks/use_api_client"
import ExpirationTimer from "./ExpirationTimer"

const SAVE_DELAY = 500

const Reservation = ({ id }) => {
  const { loading: reservationLoading, errors, reservation } = useReservation(id)
  const [sendConfirmation, setSendConfirmation] = useState(false)
  const [selectionsQueued, setSelectionsQueued] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selections, setSelections] = useState(reservation?.attendeeTypeSelections)
  const [status, setStatus] = useState(null)
  const selectionsRef = useRef([])
  const saveTimeout = useRef(null)
  selectionsRef.current = selections

  const api = useApiClient("registrations")

  useEffect(() => {
    if (!saveTimeout.current) {
      setSelections(reservation?.attendeeTypeSelections || [])
    }
  }, [reservation?.updatedAt])

  const handleSelectionsChange = selectionChanges => {
    setSelectionsQueued(true)
    clearTimeout(saveTimeout.current)

    setSelections(currentSelections =>
      currentSelections.map(selection => {
        const changes = selectionChanges.find(({ id }) => id === selection.id)
        return changes && changes.count != selection.count
          ? { ...selection, ...changes, update: true }
          : selection
      })
    )

    saveTimeout.current = setTimeout(() => {
      const selectionsToUpdate = selectionsRef.current.filter(({ update }) => update === true)

      if (selectionsToUpdate.length > 0) {
        reservation.update({
          attendee_type_selections_attributes: selectionsToUpdate.map(({ id, count }) => ({
            id,
            count
          }))
        })
      }

      setSelectionsQueued(false)
      saveTimeout.current = null
    }, SAVE_DELAY)
  }

  const redirectToSignup = useCallback(() => Turbolinks.visit(`/signups/${reservation?.eventId}`), [
    reservation?.eventId
  ])

  const handleSendConfirmationChange = ({ target: { checked } }) => setSendConfirmation(checked)

  const handleCancel = () =>
    reservation
      .destroy()
      .catch(exception => {
        if (exception?.errors && exception.errors[0].status === "404") {
          // reservation was deleted server-side
        } else {
          throw exception
        }
      })
      .finally(redirectToSignup)

  const handleRegister = () => {
    setStatus({ text: "Saving...", icon: "spinner" })
    setSubmitting(true)
    return api.post({
      url: `/reservations/${id}/complete_reservation`,
      data: {
        data: {
          attributes: {
            send_confirmation: sendConfirmation
          }
        }
      }
    })
  }

  const handleRegisterSave = () => {
    handleRegister()
      .then(result => {
        return Turbolinks.visit(`/registrations/${result.data.id}`)
      })
      .catch(() => {
        setSubmitting(false)
      })
  }

  const handleRegisterSaveAndClose = () => {
    handleRegister()
      .then(redirectToSignup)
      .catch(() => {
        setSubmitting(false)
      })
  }

  const loading = reservationLoading || submitting || selectionsQueued
  const hasNamedAttendees = reservation?.event?.registrationType === "detailed"

  return (
    <RegisterableContext.Provider
      value={{
        registerable: { ...reservation, attendeeTypeSelections: selections },
        loading,
        errors
      }}
    >
      <ExpirationTimer onCancel={handleCancel} />
      <StackView>
        <Errors />
        {!reservation && <Loading />}
        {reservation && (
          <>
            <Selections onSelectionsChange={handleSelectionsChange} />
            {hasNamedAttendees && <Attendees />}
            <RegistrationContact
              sendConfirmation={sendConfirmation}
              onSendConfirmationChange={handleSendConfirmationChange}
              hasNamedAttendees={hasNamedAttendees}
              isClearable
            />
            <Footer
              status={status}
              onCancel={handleCancel}
              onRegisterSave={handleRegisterSave}
              onRegisterSaveAndClose={handleRegisterSaveAndClose}
            />
          </>
        )}
      </StackView>
    </RegisterableContext.Provider>
  )
}

Reservation.propTypes = {
  id: string.isRequired
}

export default Reservation
